import { TheNightlyNavItem } from '@news-mono/component-library'
import { NavItem, QuickLinks } from '@news-mono/web-common'
import { RoutableNightlySection } from '@west-australian-newspapers/publication-types'

export const getLatestNavItems = (inApp: boolean): TheNightlyNavItem[] => [
    {
        name: 'Home',
        link: '/',
        section: 'default',
    },
    ...(!inApp
        ? [
              {
                  name: 'The Edition',
                  link: '/editions',
                  section: 'default' as const,
                  forceInternalSSR: true,
              },
              {
                  name: 'Puzzles',
                  link: '/puzzles',
                  section: 'default' as const,
              },
          ]
        : []),
    {
        name: 'Podcasts',
        link: '/podcasts',
        section: 'podcasts',
    },
]

export const getCommonNavItems = (inApp: boolean): TheNightlyNavItem[] => [
    {
        name: 'About',
        link: '/about',
        section: 'default',
    },
    ...(!inApp
        ? ([
              {
                  name: 'Contact',
                  link: '/contact',
                  section: 'default',
              },
          ] as TheNightlyNavItem[])
        : []),
    {
        name: 'Editorial policy',
        link: 'https://www.sevenwestmedia.com.au/assets/pdfs/Group-Editorial-Policy2.pdf',
        section: 'default',
    },
]

export const quickLinks: QuickLinks = [
    {
        name: 'Politics',
        link: '/politics',
        newWindow: false,
    },
    {
        name: 'Australia',
        link: '/australia',
        newWindow: false,
    },
    {
        name: 'Business',
        link: '/business',
        newWindow: false,
    },

    {
        name: 'Opinion',
        link: '/opinion',
        newWindow: false,
    },
    {
        name: 'Culture',
        link: '/culture',
        newWindow: false,
    },
    {
        name: 'Society',
        link: '/society',
        newWindow: false,
    },

    {
        name: 'Lifestyle',
        link: '/lifestyle',
        newWindow: false,
    },
    {
        name: 'Sport',
        link: '/sport',
        newWindow: false,
    },
    {
        name: 'World',
        link: '/world',
        newWindow: false,
    },
]

const sectionDisplayNameMap: Record<RoutableNightlySection, string> = {
    australia: 'Australia',
    opinion: 'Opinion',
    world: 'World',
    politics: 'Politics',
    business: 'Business',
    sport: 'Sport',
    society: 'Society',
    culture: 'Culture',
    lifestyle: 'Lifestyle',
    from: 'From', // NYT is the only external content provider as of this comment.
    podcasts: 'Podcasts',
}

// https://wanews.atlassian.net/wiki/spaces/REF/pages/130777119/Taxonomy+-+final+and+approved
// These must match the CUE subtopic ID for consistency!
const subTopicDisplayNameMap = {
    politics: {
        australia: 'Australia',
        world: 'World',
    },
    australia: {
        nsw: 'NSW',
        victoria: 'Victoria',
        queensland: 'Queensland',
        'south-australia': 'South Australia',
        'western-australia': 'Western Australia',
        act: 'ACT',
        tasmania: 'Tasmania',
        'northern-territory': 'Northern Territory',
    },
    business: {
        companies: 'Companies',
        markets: 'Markets',
        wealth: 'Wealth',
        mining: 'Mining',
        energy: 'Energy',
    },
    opinion: {
        editorial: 'Editorial',
        political: 'Political',
        sport: 'Sport',
        'up-late': 'Up Late',
        cartoon: 'Cartoon',
    },
    culture: {
        film: 'Film',
        tv: 'TV',
        music: 'Music',
        'pop-culture': 'Pop culture',
        'visual-arts': 'Visual arts',
        gaming: 'Gaming',
        radio: 'Radio',
        books: 'Books',
    },
    society: {
        health: 'Health',
        education: 'Education',
        'climate-change': 'Climate Change',
        science: 'Science',
        technology: 'Technology',
    },
    lifestyle: {
        travel: 'Travel',
        wellbeing: 'Wellbeing',
        property: 'Property',
        food: 'Food',
        wine: 'Wine',
        motoring: 'Motoring',
        home: 'Home',
        garden: 'Garden',
        fashion: 'Fashion',
    },
    sport: {
        afl: 'AFL',
        nrl: 'NRL',
        football: 'Football',
        cricket: 'Cricket',
        rugby: 'Rugby Union',
        other: 'Other',
    },
    world: {
        africa: 'Africa',
        americas: 'Americas',
        'asia-pacific': 'Asia Pacific',
        europe: 'Europe',
        'middle-east': 'Middle East',
        usa: 'USA',
        uk: 'UK',
    },
    from: {
        'the-new-york-times': 'The New York Times',
    },
    podcasts: {
        'the-nightly-five': 'The Nightly Five',
    },
} as const

/**
 * Subtopic NavItems for each Nightly Section.
 */
export const subTopicNavItems: Record<RoutableNightlySection, NavItem[]> = {
    politics: [
        {
            name: subTopicDisplayNameMap.politics.australia,
            link: '/politics/australia',
        },
        {
            name: subTopicDisplayNameMap.politics.world,
            link: '/politics/world',
        },
    ],
    australia: [
        {
            name: subTopicDisplayNameMap.australia.nsw,
            link: '/australia/nsw',
        },
        {
            name: subTopicDisplayNameMap.australia.victoria,
            link: '/australia/victoria',
        },
        {
            name: subTopicDisplayNameMap.australia.queensland,
            link: '/australia/queensland',
        },
        {
            name: subTopicDisplayNameMap.australia['south-australia'],
            link: '/australia/south-australia',
        },
        {
            name: subTopicDisplayNameMap.australia['western-australia'],
            link: '/australia/western-australia',
        },
        {
            name: subTopicDisplayNameMap.australia.act,
            link: '/australia/act',
        },
        {
            name: subTopicDisplayNameMap.australia.tasmania,
            link: '/australia/tasmania',
        },
        {
            name: subTopicDisplayNameMap.australia['northern-territory'],
            link: '/australia/northern-territory',
        },
    ],
    business: [
        {
            name: subTopicDisplayNameMap.business.companies,
            link: '/business/companies',
        },
        {
            name: subTopicDisplayNameMap.business.markets,
            link: '/business/markets',
        },
        {
            name: subTopicDisplayNameMap.business.wealth,
            link: '/business/wealth',
        },
        {
            name: subTopicDisplayNameMap.business.mining,
            link: '/business/mining',
        },
        {
            name: subTopicDisplayNameMap.business.energy,
            link: '/business/energy',
        },
    ],
    opinion: [
        {
            name: subTopicDisplayNameMap.opinion.editorial,
            link: '/opinion/editorial',
        },
        {
            name: subTopicDisplayNameMap.opinion.political,
            link: '/opinion/political',
        },
        {
            name: subTopicDisplayNameMap.opinion.sport,
            link: '/opinion/sport',
        },
        {
            name: subTopicDisplayNameMap.opinion['up-late'],
            link: '/opinion/up-late',
        },
        {
            name: subTopicDisplayNameMap.opinion.cartoon,
            link: '/opinion/cartoon',
        },
    ],
    from: [],
    culture: [
        {
            name: subTopicDisplayNameMap.culture.film,
            link: '/culture/film',
        },
        {
            name: subTopicDisplayNameMap.culture.tv,
            link: '/culture/tv',
        },
        {
            name: subTopicDisplayNameMap.culture.music,
            link: '/culture/music',
        },
        {
            name: subTopicDisplayNameMap.culture['pop-culture'],
            link: '/culture/pop-culture',
        },
        {
            name: subTopicDisplayNameMap.culture['visual-arts'],
            link: '/culture/visual-arts',
        },
        {
            name: subTopicDisplayNameMap.culture.gaming,
            link: '/culture/gaming',
        },
        {
            name: subTopicDisplayNameMap.culture.radio,
            link: '/culture/radio',
        },
        {
            name: subTopicDisplayNameMap.culture.books,
            link: '/culture/books',
        },
    ],
    society: [
        {
            name: subTopicDisplayNameMap.society.health,
            link: '/society/health',
        },
        {
            name: subTopicDisplayNameMap.society.education,
            link: '/society/education',
        },
        {
            name: subTopicDisplayNameMap.society['climate-change'],
            link: '/society/climate-change',
        },
        {
            name: subTopicDisplayNameMap.society.science,
            link: '/society/science',
        },
        {
            name: subTopicDisplayNameMap.society.technology,
            link: '/society/technology',
        },
    ],
    lifestyle: [
        {
            name: subTopicDisplayNameMap.lifestyle.travel,
            link: '/lifestyle/travel',
        },
        {
            name: subTopicDisplayNameMap.lifestyle.wellbeing,
            link: '/lifestyle/wellbeing',
        },
        {
            name: subTopicDisplayNameMap.lifestyle.property,
            link: '/lifestyle/property',
        },
        {
            name: subTopicDisplayNameMap.lifestyle.food,
            link: '/lifestyle/food',
        },
        {
            name: subTopicDisplayNameMap.lifestyle.wine,
            link: '/lifestyle/wine',
        },
        {
            name: subTopicDisplayNameMap.lifestyle.motoring,
            link: '/lifestyle/motoring',
        },
        {
            name: subTopicDisplayNameMap.lifestyle.home,
            link: '/lifestyle/home',
        },
        {
            name: subTopicDisplayNameMap.lifestyle.garden,
            link: '/lifestyle/garden',
        },
        {
            name: subTopicDisplayNameMap.lifestyle.fashion,
            link: '/lifestyle/fashion',
        },
    ],
    sport: [
        {
            name: subTopicDisplayNameMap.sport.afl,
            link: '/sport/afl',
        },
        {
            name: subTopicDisplayNameMap.sport.nrl,
            link: '/sport/nrl',
        },
        {
            name: subTopicDisplayNameMap.sport.football,
            link: '/sport/football',
        },
        {
            name: subTopicDisplayNameMap.sport.cricket,
            link: '/sport/cricket',
        },
        {
            name: subTopicDisplayNameMap.sport.rugby,
            link: '/sport/rugby-union',
        },
        {
            name: subTopicDisplayNameMap.sport.other,
            link: '/sport/other',
        },
    ],
    world: [
        {
            name: subTopicDisplayNameMap.world.africa,
            link: '/world/africa',
        },
        {
            name: subTopicDisplayNameMap.world.americas,
            link: '/world/americas',
        },
        {
            name: subTopicDisplayNameMap.world['asia-pacific'],
            link: '/world/asia-pacific',
        },
        {
            name: subTopicDisplayNameMap.world.europe,
            link: '/world/europe',
        },
        {
            name: subTopicDisplayNameMap.world['middle-east'],
            link: '/world/middle-east',
        },
        {
            name: subTopicDisplayNameMap.world.usa,
            link: '/world/usa',
        },
        {
            name: subTopicDisplayNameMap.world.uk,
            link: '/world/uk',
        },
    ],
    podcasts: [
        {
            name: subTopicDisplayNameMap.podcasts['the-nightly-five'],
            link: '/podcasts/the-nightly-five',
        },
    ],
}

/**
 * All NavItems for each Nightly Section. Incl 'All' Links, etc.
 */
export const sectionNavItems: Record<
    RoutableNightlySection,
    TheNightlyNavItem
> = {
    politics: {
        name: sectionDisplayNameMap.politics,
        link: '/politics',
        section: 'politics',
        subNavLinks: [
            {
                name: 'All Politics',
                link: '/politics',
            },
            ...subTopicNavItems.politics,
        ],
    },
    australia: {
        name: sectionDisplayNameMap.australia,
        link: '/australia',
        section: 'australia',
        subNavLinks: [
            {
                name: 'All Australia',
                link: '/australia',
            },
            ...subTopicNavItems.australia,
        ],
    },
    business: {
        name: sectionDisplayNameMap.business,
        link: '/business',
        section: 'business',
        subNavLinks: [
            {
                name: 'All Business',
                link: '/business',
            },
            ...subTopicNavItems.business,
        ],
    },
    opinion: {
        name: sectionDisplayNameMap.opinion,
        link: '/opinion',
        section: 'opinion',
        subNavLinks: [
            {
                name: 'All Opinion',
                link: '/opinion',
            },
            ...subTopicNavItems.opinion,
        ],
    },
    from: {
        name: subTopicDisplayNameMap.from['the-new-york-times'], // ! WARNING !
        link: '/from/the-new-york-times', // This is because NYT is the only one so far.
        section: 'from', // Taxonomy may need changes if things are assumed incorrectly.
        subNavLinks: [
            {
                name: 'All Articles',
                link: '/from/the-new-york-times',
            },
        ],
    },
    culture: {
        name: sectionDisplayNameMap.culture,
        link: '/culture',
        section: 'culture',
        subNavLinks: [
            {
                name: 'All Culture',
                link: '/culture',
            },
            ...subTopicNavItems.culture,
        ],
    },
    society: {
        name: sectionDisplayNameMap.society,
        link: '/society',
        section: 'society',
        subNavLinks: [
            {
                name: 'All Society',
                link: '/society',
            },
            ...subTopicNavItems.society,
        ],
    },
    lifestyle: {
        name: sectionDisplayNameMap.lifestyle,
        link: '/lifestyle',
        section: 'lifestyle',
        subNavLinks: [
            {
                name: 'All Lifestyle',
                link: '/lifestyle',
            },
            ...subTopicNavItems.lifestyle,
        ],
    },
    sport: {
        name: sectionDisplayNameMap.sport,
        link: '/sport',
        section: 'sport',
        subNavLinks: [
            {
                name: 'All Sport',
                link: '/sport',
            },
            ...subTopicNavItems.sport,
        ],
    },
    world: {
        name: sectionDisplayNameMap.world,
        link: '/world',
        section: 'world',
        subNavLinks: [
            {
                name: 'All World',
                link: '/world',
            },
            ...subTopicNavItems.world,
        ],
    },
    podcasts: {
        name: sectionDisplayNameMap.podcasts,
        link: '/podcasts',
        section: 'podcasts',
        subNavLinks: [
            {
                name: 'All Podcasts',
                link: '/podcasts',
            },
            ...subTopicNavItems.podcasts,
        ],
        hideFromFlyoutMenu: true,
    },
}
